import { useEffect, useState } from "react";
import { CloseIcon } from "./icons";
import Spinner from "./Spinner";
import { cancelApplication, getCancelDetails } from "../api/leadApi";
import { isApiSuccess } from "../utils/apiResponse";
import { BANK_STATUS, REMARK_MAX_LENGTH } from "../data/constants";

// Cancel the whole application: reason + remark. Every open bank becomes
// Cancelled and the application moves to Lost (read-only in SFA).
// `target` = { leadId, loanNo?, financierId? } or null.
export default function CancelApplicationModal({ target, onClose, onCancelled, flash }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reasonId, setReasonId] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDetails(null);
    setReasonId("");
    setRemark("");
    if (!target?.leadId) return;
    let alive = true;
    setLoading(true);
    getCancelDetails(target.leadId)
      .then(({ data }) => {
        if (!alive) return;
        if (isApiSuccess(data)) setDetails(data.data || null);
        else flash?.(data?.message || "Could not load cancellation details");
      })
      .catch((err) => alive && flash?.(err.response?.data?.message || "Could not load cancellation details"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // Load once per opened target; flash is a fresh function every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!target) return null;

  const reasons = details?.reasons || [];
  // Only open banks are cancelled; ones already cancelled stay as they are.
  const banks = (details?.financiers || []).filter((b) => b.bank_status?.code !== BANK_STATUS.CANCELLED);
  const blocked = details && !details.can_cancel_application;
  const incomplete = !reasonId || !remark.trim();

  const submit = async () => {
    if (incomplete || saving || blocked) return;
    setSaving(true);
    try {
      const { data } = await cancelApplication(target.leadId, reasonId, remark.trim(), target.financierId);
      if (!isApiSuccess(data)) {
        flash?.(data?.message || "Could not cancel the application");
        return;
      }
      flash?.(data.message || "Application cancelled");
      onCancelled?.(data.data);
    } catch (err) {
      flash?.(err.response?.data?.message || "Could not cancel the application");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="overlay overlay--mail" onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className="mail-box action-box">
        <div className="mail-box__head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="action-box__title">Cancel whole application</div>
            {target.loanNo && <div className="action-box__sub">Application {target.loanNo}</div>}
          </div>
          <button type="button" className="sheet-box__close" onClick={onClose} disabled={saving} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className="mail-box__body">
          {loading && <Spinner page label="Loading…" />}
          {!loading && blocked && (
            <div className="bank-state-note bank-state-note--danger">
              {details.cancel_note || "This application can no longer be cancelled from SFA."}
            </div>
          )}
          {!loading && details && !blocked && (
            <>
              <div>
                <span className="mail-box__field-label">Banks that will be cancelled</span>
                {banks.length === 0 ? (
                  <div className="cancel-app__empty">No bank has been sent yet.</div>
                ) : (
                  <ul className="cancel-app__banks">
                    {banks.map((b) => (
                      <li key={b.financier_id}>
                        <span>{b.financier_name || "Bank " + b.financier_id}</span>
                        <span className={"stage-chip stage-chip--" + (b.bank_status?.code || "pending")}>{b.bank_status?.label || "Pending"}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className="mail-box__field-label" htmlFor="cancel-app-reason">
                  Reason <span className="field__req">*</span>
                </label>
                <select
                  id="cancel-app-reason"
                  className={"mail-box__input action-box__select" + (reasonId ? "" : " action-box__select--empty")}
                  value={reasonId}
                  onChange={(e) => setReasonId(e.target.value)}
                >
                  <option value="" disabled>
                    Select reason
                  </option>
                  {reasons.map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mail-box__field-label" htmlFor="cancel-app-remark">
                  Remark <span className="field__req">*</span>
                </label>
                <textarea
                  id="cancel-app-remark"
                  className="mail-box__textarea"
                  rows={4}
                  maxLength={REMARK_MAX_LENGTH}
                  placeholder="Write the reason for cancelling"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
                <div className="action-box__hint">Cancellation date is recorded as today.</div>
              </div>

              <div className="action-box__warn">
                Every open bank above is cancelled and the application moves to Lost. This cannot be undone.
              </div>
            </>
          )}
        </div>

        <div className="mail-box__foot">
          <button type="button" className="mail-box__cancel" onClick={onClose} disabled={saving}>
            Close
          </button>
          <button
            type="button"
            className="mail-box__send mail-box__send--danger"
            onClick={submit}
            disabled={saving || loading || !details || blocked || incomplete}
          >
            {saving ? "Cancelling…" : "Cancel application"}
          </button>
        </div>
      </div>
    </div>
  );
}
